// =============================================================================
// R-239 P4 Task 10 — ReverifyValveConfig. Job-type-level control for the
// re-verify valve. Controlled component: value + onChange (or onSave) pattern
// mirroring IntelligenceTab (verifyAutoCorrections toggle → handleSave).
// Validates with parseReverifyValve before emitting; blocks save + shows error
// on invalid input (e.g., auto without sampleEveryN).
// =============================================================================

import { useState } from "react";
import {
  parseReverifyValve,
  type ReverifyValve,
  type ReverifyMode,
} from "@capture/shared";
import styles from "./ReverifyValveConfig.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ReverifyValveConfigProps {
  /** Current valve from the job type (default: DEFAULT_REVERIFY_VALVE). */
  value: ReverifyValve;
  /** Called with the validated valve on save. */
  onChange: (next: ReverifyValve) => void;
  /** Whether a save operation is in progress (disables the save button). */
  isSaving?: boolean;
}

// ---------------------------------------------------------------------------
// Mode label map
// ---------------------------------------------------------------------------

const MODE_LABELS: Record<ReverifyMode, string> = {
  off: "Off",
  auto: "Auto-sample",
  targeted: "Targeted",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ReverifyValveConfig({
  value,
  onChange,
  isSaving = false,
}: ReverifyValveConfigProps) {
  // Local draft state — mirrors the incoming value on first render.
  const [mode, setMode] = useState<ReverifyMode>(value.mode);
  const [sampleEveryN, setSampleEveryN] = useState<string>(
    value.sampleEveryN != null ? String(value.sampleEveryN) : "",
  );
  const [targetedFields, setTargetedFields] = useState<string[]>(
    value.targetedFields ?? [],
  );
  const [targetedUntil, setTargetedUntil] = useState<string>(
    value.targetedUntil ?? "",
  );
  const [fieldInput, setFieldInput] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Build a candidate valve from current draft state.
  function buildCandidate(): unknown {
    if (mode === "off") return { mode: "off" };
    if (mode === "auto") {
      const n = sampleEveryN.trim() === "" ? undefined : Number(sampleEveryN);
      return { mode: "auto", sampleEveryN: n };
    }
    // targeted
    const valve: Record<string, unknown> = { mode: "targeted", targetedFields };
    if (targetedUntil.trim() !== "") valve.targetedUntil = targetedUntil.trim();
    return valve;
  }

  function handleSave() {
    const candidate = buildCandidate();
    const result = parseReverifyValve(candidate);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    onChange(result.value);
  }

  function handleModeChange(next: ReverifyMode) {
    setMode(next);
    setError(null);
    // Reset sub-fields when switching away from a mode to avoid stale partial data.
    if (next !== "auto") setSampleEveryN("");
    if (next !== "targeted") {
      setTargetedFields([]);
      setTargetedUntil("");
      setFieldInput("");
    }
  }

  function handleAddField() {
    const trimmed = fieldInput.trim();
    if (!trimmed) return;
    if (!targetedFields.includes(trimmed)) {
      setTargetedFields((prev) => [...prev, trimmed]);
    }
    setFieldInput("");
  }

  function handleRemoveField(name: string) {
    setTargetedFields((prev) => prev.filter((f) => f !== name));
  }

  return (
    <div className={styles.root}>
      <p className={styles.helper}>
        Re-verify valve — per tenant + job · default off. Periodically
        re-presents a would-be-skipped field so drift can be caught.
      </p>

      <div className={styles.row}>
        <label htmlFor="reverify-mode" className={styles.label}>
          Mode
        </label>
        <select
          id="reverify-mode"
          className={styles.select}
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as ReverifyMode)}
        >
          {(Object.keys(MODE_LABELS) as ReverifyMode[]).map((m) => (
            <option key={m} value={m}>
              {MODE_LABELS[m]}
            </option>
          ))}
        </select>
      </div>

      {mode === "auto" && (
        <div className={styles.row}>
          <label htmlFor="reverify-sample-n" className={styles.label}>
            Sample every N
          </label>
          <input
            id="reverify-sample-n"
            type="number"
            min={1}
            step={1}
            className={styles.numberInput}
            value={sampleEveryN}
            onChange={(e) => {
              setSampleEveryN(e.target.value);
              setError(null);
            }}
            placeholder="e.g. 10"
            aria-label="Sample every N documents"
          />
        </div>
      )}

      {mode === "targeted" && (
        <div className={styles.targetedSection}>
          <div className={styles.row}>
            <label htmlFor="reverify-field-input" className={styles.label}>
              Field name
            </label>
            <input
              id="reverify-field-input"
              type="text"
              className={styles.textInput}
              value={fieldInput}
              onChange={(e) => setFieldInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddField();
                }
              }}
              placeholder="Field name"
              aria-label="Field name to add"
            />
            <button
              type="button"
              className={styles.addBtn}
              onClick={handleAddField}
              aria-label="Add field"
            >
              Add field
            </button>
          </div>

          {targetedFields.length > 0 && (
            <ul className={styles.fieldList}>
              {targetedFields.map((f) => (
                <li key={f} className={styles.fieldItem}>
                  <span>{f}</span>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => handleRemoveField(f)}
                    aria-label={`Remove ${f}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className={styles.row}>
            <label htmlFor="reverify-until" className={styles.label}>
              Until (optional)
            </label>
            <input
              id="reverify-until"
              type="datetime-local"
              className={styles.textInput}
              value={targetedUntil}
              onChange={(e) => setTargetedUntil(e.target.value)}
              aria-label="Target until date-time"
            />
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className="admin-btn admin-btn--primary"
          onClick={handleSave}
          disabled={isSaving}
          aria-label="Save reverify valve"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
